import { create } from 'zustand';
import { shallow } from 'zustand/shallow';
import { GeneratedHourProps, timeZoneDisplayNames, generateDayHours, TimeZone } from './RuleTimes/utils';
import { IScheduledActionsGroup } from 'src/store/modules/adManager/adScheduledActionGroups/types';

export interface RuleConditionsProps {
  ruleStrategy: 'increase' | 'decrease';
  ruleTarget: 'adCampaign' | 'adTarget';
  ruleStrategyMetric: '%' | '$';
  value?: number;
  ruleStrategyUpperBound?: number;
  ruleStrategyLowerBound?: number;
}

enum Days {
  Sun = 'Sun',
  Mon = 'Mon',
  Tues = 'Tues',
  Wed = 'Wed',
  Thu = 'Thu',
  Fri = 'Fri',
  Sat = 'Sat'
}

type DayState = Record<Days, boolean>;

const times = generateDayHours();

// :) Help, The voices in my head are getting louder

const initialState = {
  ruleName: '',
  times: generateDayHours(),
  adRules: [],
  selectedTimeZone: Object.keys(timeZoneDisplayNames)[0] as TimeZone,
  startTime: times[0],
  endTime: times[20],
  editRuleId: undefined,
  rules: {
    ruleStrategy: 'increase' as 'increase' | 'decrease',
    ruleTarget: 'adCampaign' as 'adCampaign' | 'adTarget',
    ruleStrategyMetric: '%' as '%' | '$'
  },
  days: {
    Sun: false,
    Mon: false,
    Tues: false,
    Wed: false,
    Thu: false,
    Fri: false,
    Sat: false
  },
  campaigns: [],
  selectedCampaigns: new Map(),
  targets: [],
  selectedTargets: new Map(),
  searchTerm: ''
};

export interface RuleStoreProps {
  // scheduleGroupName
  ruleName: string;
  selectedTimeZone: TimeZone;
  adRules: IScheduledActionsGroup[];
  times: GeneratedHourProps[];
  startTime: GeneratedHourProps | undefined;
  endTime: GeneratedHourProps | undefined;
  rules: RuleConditionsProps;
  days: DayState;
  campaigns: Record<string, any>[];
  targets: Record<string, any>[];
  selectedTargets: Map<string, Record<string, any>>;
  selectedCampaigns: Map<string, Record<string, any>>;
  editRuleId?: string;
  searchTerm: string;

  // Actions
  setRuleName: (name: string) => void;
  setSelectedTimeZone: (zone: TimeZone) => void;
  setStartTime: (time: GeneratedHourProps) => void;
  setEndTime: (time: GeneratedHourProps) => void;
  setRules: (rules: RuleConditionsProps) => void;
  setCampaigns: (campaigns: Record<string, any>[]) => void;
  setTargets: (targets: Record<string, any>[]) => void;
  toggleDay: (day: Days) => void;
  setSearchTerm: (searchTerm: string) => void;

  // Campaigns
  addSelectedCampaigns: (campaigns: Record<string, any>) => void;
  removeSelectedCampaigns: (campaigns: string) => void;
  removeAllSelectedCampaigns: () => void;
  addAllSelectedCampaigns: () => void;

  // Targets
  addSelectedTargets: (campaigns: Record<string, any>) => void;
  removeSelectedTargets: (campaigns: string) => void;
  removeAllSelectedTargets: () => void;
  addAllSelectedTargets: () => void;

  // Editing Rules
  setEditRuleId: (id: string) => void;
  removeEditRuleId: () => void;
  setRuleStoreData: (
    data: Pick<
      RuleStoreProps,
      | 'selectedTimeZone'
      | 'times'
      | 'startTime'
      | 'endTime'
      | 'rules'
      | 'days'
      | 'selectedTargets'
      | 'selectedCampaigns'
    >
  ) => void;
  purgeRuleStore: () => void;
}

export const RuleStorePropsEntity = create<RuleStoreProps>((set) => ({
  // initial state
  ...initialState,
  // actions
  setAdRules: (adRules) => set({ adRules }),
  setRuleName: (name) => set({ ruleName: name }),
  setSelectedTimeZone: (zone) => set({ selectedTimeZone: zone }),
  setStartTime: (time) => set({ startTime: time }),
  setEndTime: (time) => set({ endTime: time }),
  setRules: (rules) => set({ rules }),
  toggleDay: (day: Days) =>
    set((state: RuleStoreProps) => ({
      days: {
        ...state.days,
        [day]: !state.days[day]
      }
    })),
  setCampaigns: (campaigns) => set({ campaigns }),
  setSearchTerm: (searchTerm) => set({ searchTerm }),
  addSelectedCampaigns: (campaigns: Record<string, any>) =>
    set((state: RuleStoreProps) => {
      const newCampaigns = new Map(state.selectedCampaigns);
      newCampaigns.set(campaigns.campaignId, campaigns);
      return { selectedCampaigns: newCampaigns };
    }),
  removeSelectedCampaigns: (campaigns: string) =>
    set((state: RuleStoreProps) => {
      const newCampaigns = new Map(state.selectedCampaigns);
      newCampaigns.delete(campaigns);
      return { selectedCampaigns: newCampaigns };
    }),
  removeAllSelectedCampaigns: () => set({ selectedCampaigns: new Map() }),
  addAllSelectedCampaigns: () =>
    set((state: RuleStoreProps) => {
      // Filter campaigns by search term if present
      const filteredCampaigns = state.searchTerm
        ? state.campaigns.filter(
            (campaign) =>
              campaign.campaignName?.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
              campaign.campaign?.campaignName?.toLowerCase().includes(state.searchTerm.toLowerCase())
          )
        : state.campaigns;
      
      return { selectedCampaigns: new Map(filteredCampaigns.map((x) => [x.campaignId, x])) };
    }),
  setTargets: (targets) => set({ targets }),
  addSelectedTargets: (targets: Record<string, any>) =>
    set((state: RuleStoreProps) => {
      const newTargets = new Map(state.selectedTargets);
      newTargets.set(`${targets.targetingText}_${targets.targetingType}`, targets);
      return { selectedTargets: newTargets };
    }),
  removeSelectedTargets: (targets: string) =>
    set((state: RuleStoreProps) => {
      const newTargets = new Map(state.selectedTargets);
      newTargets.delete(targets);
      return { selectedTargets: newTargets };
    }),
  removeAllSelectedTargets: () => set({ selectedTargets: new Map() }),
  addAllSelectedTargets: () =>
    set((state: RuleStoreProps) => {
      // Filter targets by search term if present
      const filteredTargets = state.searchTerm
        ? state.targets.filter((target) =>
            target.targetingText?.toLowerCase().includes(state.searchTerm.toLowerCase())
          )
        : state.targets;
      
      return { selectedTargets: new Map(filteredTargets.map((x) => [`${x.targetingText}_${x.targetingType}`, x])) };
    }),
  setEditRuleId: (id) => set({ editRuleId: id }),
  removeEditRuleId: () => set({ editRuleId: undefined }),
  setRuleStoreData: (data) => set(data),
  purgeRuleStore: () => set({ ...initialState })
}));

const useRuleStoreShallow = (selector: (state: RuleStoreProps) => any) => RuleStorePropsEntity(selector, shallow);

export default useRuleStoreShallow;
